import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Store, Users, Package, BarChart3, Tag, ShieldCheck,
  Zap, CreditCard, Boxes,
} from "lucide-react";
import { LegalLinks } from "@/components/LegalLinks";

const features = [
  { icon: Tag, title: "Customer-specific pricing", desc: "Give every customer or group their own prices. Set them from the product or the customer profile in one click." },
  { icon: Store, title: "Private storefronts", desc: "Public browsing, invite-only, or fully private. You decide who sees products, pricing, and can place orders." },
  { icon: Package, title: "Products & inventory", desc: "SKUs, images, categories, stock adjustments, history, and low-stock alerts that keep you ahead." },
  { icon: Users, title: "Lightweight CRM", desc: "Lifetime sales, order counts, tags, and notes. Instantly know who your best customers are." },
  { icon: CreditCard, title: "Simple checkout", desc: "Zelle, Venmo, Cash App, PayPal. Customers upload proof, you approve, statuses update automatically." },
  { icon: BarChart3, title: "Clean reporting", desc: "Sales today, this week, this month, top products, revenue, and inventory alerts at a glance." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Sign in</Button></Link>
            <Link to="/register"><Button className="rounded-full" data-testid="nav-register-btn">Start free</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground mb-6">
              <Zap className="h-3.5 w-3.5 text-primary" /> The operating system for specialized commerce
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Commerce with{" "}
              <span className="text-primary">customer-specific pricing</span>, built in.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Drelik combines the simplicity of a modern storefront with private catalogs,
              per-customer pricing, inventory, and a lightweight CRM. Launch your store in minutes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button size="lg" className="rounded-full h-12 px-7 text-base w-full sm:w-auto" data-testid="hero-start-btn">
                  Create your store <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base w-full sm:w-auto" data-testid="hero-signin-btn">
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Free to start</span>
              <span className="flex items-center gap-1.5"><Boxes className="h-4 w-4 text-primary" /> No card required</span>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="group rounded-xl border border-border bg-card p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-md hover:border-primary/40 animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`feature-card-${i}`}
            >
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                <f.icon className="h-5 w-5 text-primary" strokeWidth={2} />
              </div>
              <h3 className="text-lg font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-28">
        <div className="rounded-2xl bg-primary px-8 py-14 sm:px-14 text-center relative overflow-hidden">
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground tracking-tight">Ready to build your storefront?</h2>
            <p className="mt-3 text-primary-foreground/80 max-w-xl mx-auto">
              Join businesses running smarter commerce with Drelik. Set up your store, add products, and invite customers today.
            </p>
            <Link to="/register">
              <Button size="lg" variant="secondary" className="mt-7 rounded-full h-12 px-8 text-base" data-testid="cta-start-btn">
                Get started free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <Logo />
            <span>© {new Date().getFullYear()} Drelik · admin@drelik.com</span>
          </div>
          <LegalLinks className="justify-center sm:justify-start" />
        </div>
      </footer>
    </div>
  );
}
